/**
 * Reference generation for manual payment posting.
 *
 * Cash receipts have no external transaction id (unlike a bank transfer ref
 * or an M-Pesa tx id), so operators shouldn't have to invent one. For the
 * `manual_cash` channel the admin forms auto-fill a generated reference of the
 * form `CASH-YYYYMMDD-XXXXXXXX`.
 *
 * The value is generated ONCE per form fill and held in the form's state, so a
 * network retry re-sends the SAME reference — the server's
 * `(channel, providerReference)` idempotency gate then dedups it instead of
 * double-posting. Two genuinely-distinct cash receipts get distinct refs.
 */

/** True when `ref` looks like one of our auto-generated cash references. */
export function isGeneratedCashReference(ref: string): boolean {
  return /^CASH-\d{8}-[0-9A-F]{8}$/.test(ref);
}

/**
 * Generate a cash reference `CASH-YYYYMMDD-XXXXXXXX`. The suffix is 8 hex
 * chars from `crypto.randomUUID()` — the same CSPRNG the device-fingerprint
 * uses (see lib/tokens.ts); never `Math.random`.
 */
export function generateCashReference(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const suffix = crypto.randomUUID().replace(/-/g, "").slice(0, 8).toUpperCase();
  return `CASH-${y}${m}${d}-${suffix}`;
}
